import axios from 'axios'

const ACCESS_KEY = 'token'
const REFRESH_KEY = 'refresh_token'

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 60000, // 60 seconds for large history queries
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor — แนบ access token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_KEY)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// ---- 401 auto-refresh -------------------------------------------------------
// access token อายุสั้น (120 นาที) แต่หมดกลางทางได้ — ตอนเจอ 401 ให้ลอง refresh
// ด้วย refresh token แล้ว retry request เดิมแบบเนียนๆ ไม่เด้ง user ออก
// isRefreshing + queue: ถ้าหลาย request โดน 401 พร้อมกัน ให้ refresh ครั้งเดียว
// ที่เหลือรอ token ใหม่แล้วค่อย retry (กันยิง /token/refresh/ ซ้ำหลายรอบ)
let isRefreshing = false
let pendingQueue: { resolve: (token: string) => void; reject: (err: unknown) => void }[] = []

function flushQueue(error: unknown, token: string | null) {
  pendingQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token as string)))
  pendingQueue = []
}

function forceLogout() {
  localStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
  // hard redirect (เลี่ยง import router ตรงนี้ กัน circular import) — จำ path
  // เดิมไว้ให้ login เด้งกลับหลังเข้าใหม่
  if (window.location.pathname !== '/login') {
    const redirect = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.assign(`/login?redirect=${redirect}`)
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config
    const status = error.response?.status
    const url: string = original?.url || ''

    // ไม่ retry: ไม่ใช่ 401, retry ไปแล้ว, หรือเป็นเส้น auth เอง (login/refresh)
    // — กัน loop เพราะ 401 ของ /token/refresh/ เองแปลว่า refresh token หมดจริง
    const isAuthEndpoint = url.includes('/auth/token')
    if (status !== 401 || !original || original._retry || isAuthEndpoint) {
      return Promise.reject(error)
    }

    const refresh = localStorage.getItem(REFRESH_KEY)
    if (!refresh) {
      forceLogout()
      return Promise.reject(error)
    }

    // มี refresh กำลังทำอยู่ → เข้าคิวรอ token ใหม่แล้ว retry
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token) => {
            original._retry = true
            original.headers.Authorization = `Bearer ${token}`
            resolve(apiClient(original))
          },
          reject,
        })
      })
    }

    original._retry = true
    isRefreshing = true
    try {
      // ผ่าน apiClient ได้ เพราะ isAuthEndpoint guard กัน 401 ของเส้นนี้ไม่ให้วน
      const { data } = await apiClient.post<{ access: string }>('/auth/token/refresh/', { refresh })
      const newToken = data.access
      localStorage.setItem(ACCESS_KEY, newToken)
      flushQueue(null, newToken)
      original.headers.Authorization = `Bearer ${newToken}`
      return apiClient(original)
    } catch (refreshError) {
      // refresh token หมด/ใช้ไม่ได้ → เด้งออกไป login
      flushQueue(refreshError, null)
      forceLogout()
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

export default apiClient
